/**
 * LUSTR — Luxury Shopify Theme
 * Animations & Interactions
 */

(function() {
  'use strict';

  // ═══════════════════════════════════════════
  // Floating Petals Canvas
  // ═══════════════════════════════════════════

  function initFloatingPetals() {
    const canvas = document.getElementById('petals-canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let isActive = true;

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }

    resize();
    window.addEventListener('resize', resize);

    const PETAL_COUNT = 30;
    const petals = [];

    function createPetal() {
      return {
        x: Math.random() * canvas.width,
        y: -30 - Math.random() * 100,
        width: 12 + Math.random() * 12,
        height: 8 + Math.random() * 8,
        speedY: 0.3 + Math.random() * 0.9,
        speedX: (Math.random() - 0.5) * 0.3,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: -0.01 + Math.random() * 0.02,
        swayAmplitude: 20 + Math.random() * 30,
        swayFrequency: 0.005 + Math.random() * 0.015,
        swayPhase: Math.random() * Math.PI * 2,
        opacity: 0.08 + Math.random() * 0.07
      };
    }

    for (let i = 0; i < PETAL_COUNT; i++) {
      const petal = createPetal();
      petal.y = Math.random() * canvas.height;
      petals.push(petal);
    }

    function drawPetal(petal) {
      ctx.save();
      ctx.translate(petal.x, petal.y);
      ctx.rotate(petal.rotation);
      ctx.globalAlpha = petal.opacity;
      ctx.fillStyle = '#7a1428';

      // Draw petal shape using bezier curves
      ctx.beginPath();
      ctx.moveTo(0, -petal.height / 2);
      ctx.bezierCurveTo(
        petal.width / 2, -petal.height / 4,
        petal.width / 2, petal.height / 4,
        0, petal.height / 2
      );
      ctx.bezierCurveTo(
        -petal.width / 2, petal.height / 4,
        -petal.width / 2, -petal.height / 4,
        0, -petal.height / 2
      );
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    function animate() {
      if (!isActive) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const time = Date.now();

      petals.forEach(petal => {
        petal.y += petal.speedY;
        petal.x += petal.speedX + Math.sin(time * petal.swayFrequency + petal.swayPhase) * 0.3;
        petal.rotation += petal.rotationSpeed;

        if (petal.y > canvas.height + 30) {
          Object.assign(petal, createPetal());
        }

        drawPetal(petal);
      });

      animationId = requestAnimationFrame(animate);
    }

    // IntersectionObserver to pause when not visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          isActive = true;
          animate();
        } else {
          isActive = false;
          cancelAnimationFrame(animationId);
        }
      });
    }, { threshold: 0.1 });

    observer.observe(canvas);
    animate();
  }

  // ═══════════════════════════════════════════
  // Navigation Scroll Behavior
  // ═══════════════════════════════════════════

  function initNavigation() {
    const nav = document.querySelector('.site-nav');
    if (!nav) return;

    const hero = document.querySelector('.hero');
    if (!hero) {
      nav.classList.add('site-nav--solid');
      nav.classList.remove('site-nav--transparent');
      return;
    }

    let lastScroll = 0;

    function updateNav() {
      const scrollY = window.scrollY;
      const heroBottom = hero.offsetTop + hero.offsetHeight;

      if (scrollY > heroBottom - 72) {
        nav.classList.add('site-nav--solid');
        nav.classList.remove('site-nav--transparent');
      } else {
        nav.classList.add('site-nav--transparent');
        nav.classList.remove('site-nav--solid');
      }

      lastScroll = scrollY;
    }

    window.addEventListener('scroll', updateNav, { passive: true });
    updateNav();
  }

  // ═══════════════════════════════════════════
  // Mobile Menu Toggle
  // ═══════════════════════════════════════════

  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-toggle]');
    const overlay = document.querySelector('[data-mobile-overlay]');
    const close = document.querySelector('[data-mobile-close]');

    if (!toggle || !overlay) return;

    function open() {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeMenu() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    toggle.addEventListener('click', open);
    if (close) close.addEventListener('click', closeMenu);

    // Close on link click
    overlay.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', closeMenu);
    });
  }

  // ═══════════════════════════════════════════
  // Scroll-Triggered Reveals
  // ═══════════════════════════════════════════

  function initScrollReveals() {
    const revealElements = document.querySelectorAll('.reveal');
    if (!revealElements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(el => observer.observe(el));
  }

  // ═══════════════════════════════════════════
  // Product Image Thumbnail Switcher
  // ═══════════════════════════════════════════

  function initProductThumbnails() {
    const thumbs = document.querySelectorAll('[data-product-thumb]');
    const mainImage = document.querySelector('[data-product-main-image]');

    if (!thumbs.length || !mainImage) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.dataset.productThumb;
        const newAlt = thumb.dataset.productThumbAlt || '';

        // Fade out
        mainImage.style.opacity = '0';
        mainImage.style.transition = 'opacity 0.3s ease';

        setTimeout(() => {
          mainImage.src = newSrc;
          mainImage.alt = newAlt;
          mainImage.style.opacity = '1';
        }, 300);

        // Update active state
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  }

  // ═══════════════════════════════════════════
  // Accordion
  // ═══════════════════════════════════════════

  function initAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const item = accordion.closest('.accordion__item');
      const content = item.querySelector('.accordion__content');
      const contentInner = item.querySelector('.accordion__content-inner');

      if (!content || !contentInner) return;

      accordion.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');

        // Close all other items
        item.closest('.accordion')?.querySelectorAll('.accordion__item.is-open').forEach(openItem => {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            const openContent = openItem.querySelector('.accordion__content');
            if (openContent) openContent.style.maxHeight = '0';
          }
        });

        if (isOpen) {
          item.classList.remove('is-open');
          content.style.maxHeight = '0';
        } else {
          item.classList.add('is-open');
          content.style.maxHeight = contentInner.scrollHeight + 'px';
        }
      });
    });
  }

  // ═══════════════════════════════════════════
  // Cart Drawer
  // ═══════════════════════════════════════════

  function initCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    const overlay = drawer.querySelector('[data-cart-overlay]');
    const close = drawer.querySelector('[data-cart-close]');
    const cartBtns = document.querySelectorAll('[data-cart-toggle]');

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    cartBtns.forEach(btn => btn.addEventListener('click', open));
    if (overlay) overlay.addEventListener('click', closeDrawer);
    if (close) close.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });
  }

  // ═══════════════════════════════════════════
  // Smooth Scroll for Anchor Links
  // ═══════════════════════════════════════════

  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });
  }

  // ═══════════════════════════════════════════
  // Contact Form Handler
  // ═══════════════════════════════════════════

  function initContactForm() {
    const form = document.querySelector('[data-contact-form]');
    if (!form) return;

    const successMessage = form.querySelector('[data-contact-success]');

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      // In a real Shopify theme, this would submit via AJAX
      // For demo, show success state
      form.style.display = 'none';
      if (successMessage) {
        successMessage.style.display = 'block';
      }
    });
  }

  // ═══════════════════════════════════════════
  // NFC Experience Icon Morph
  // ═══════════════════════════════════════════

  function initNFCMorph() {
    const section = document.querySelector('.nfc-experience');
    if (!section) return;

    const icons = section.querySelectorAll('.nfc-experience__icon');
    if (!icons.length) return;

    let currentIndex = 0;

    function cycleIcons() {
      icons.forEach((icon, i) => {
        icon.classList.toggle('is-active', i === currentIndex);
      });
      currentIndex = (currentIndex + 1) % icons.length;
    }

    // Start with first icon visible
    icons[0].classList.add('is-active');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          cycleIcons();
          section._morphInterval = setInterval(cycleIcons, 2500);
        } else {
          if (section._morphInterval) {
            clearInterval(section._morphInterval);
          }
        }
      });
    }, { threshold: 0.3 });

    observer.observe(section);
  }

  // ═══════════════════════════════════════════
  // Hero Entrance Animation
  // ═══════════════════════════════════════════

  function initHeroEntrance() {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    const logo = hero.querySelector('.hero__logo-mark');
    const line = hero.querySelector('.hero__logo-line');
    const headline = hero.querySelector('.hero__headline');
    const subtext = hero.querySelector('.hero__subtext');
    const cta = hero.querySelector('.hero__cta');

    const elements = [logo, line, headline, subtext, cta].filter(Boolean);

    elements.forEach((el, i) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = `opacity 1s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.2}s, transform 1s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.2}s`;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        elements.forEach(el => {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        });
      });
    });
  }

  // ═══════════════════════════════════════════
  // Initialize All
  // ═══════════════════════════════════════════

  function init() {
    initFloatingPetals();
    initNavigation();
    initMobileMenu();
    initScrollReveals();
    initProductThumbnails();
    initAccordions();
    initCartDrawer();
    initSmoothScroll();
    initContactForm();
    initNFCMorph();
    initHeroEntrance();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
